<template>
  <div class="action-card" :class="cardClass">
    <!-- VueFlow Handle - Top (Input) -->
    <Handle 
      type="target" 
      :position="Position.Top" 
      class="action-handle action-handle--top"
      :class="{ 'action-handle--connected': hasIncomingConnection }"
    />

    <div class="action-card-body">
      <!-- Header Section -->
      <div class="action-card-header" :class="headerClass">
        <div class="action-icon" :class="iconClass">
          <div class="action-icon-svg">
            <slot name="icon"></slot>
          </div>
        </div>

        <div class="action-header-info">
          <div class="action-title">{{ title }}</div>
          <div class="action-subtitle">{{ subtitle }}</div>

          <div v-if="!isConfigured" class="action-status-row">
            <div class="action-status-icon">
              <img src="@/assets/MyWorkBench/Workflow/warning_icon_triangel.svg" style="width: 20px; height: 20px; margin-right: 4px;" />
            </div>
            <div class="action-status-text action-status-text--error">Needs Configuration.</div>
          </div>

          <div v-else class="action-status-row">
            <div class="action-status-icon">
              <svg viewBox="0 0 15 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.65833 8.81667L1.18333 5.34167L0 6.51667L4.65833 11.175L14.6583 1.175L13.4833 0L4.65833 8.81667Z" fill="#0A6629"/>
              </svg>
            </div>
            <div class="action-status-text action-status-text--success">Configured</div>
          </div>
        </div>
      </div>

      <!-- Content Slot - Different for each action type -->
      <slot name="content"></slot>

      <!-- Buttons Section -->
      <div class="action-buttons">
        <ResponsiveButton
          button_type="outlined"
          built_in_theme="error"
          size="small"
          display_name="Delete"
          :disabled="isNodeButtonDisabled()"
          customized_class="btn-delete"
          @click-button="onDeleteClick"
        >
          <template #prefix>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M11 0V1H16V3H15V16C15 17.1 14.1 18 13 18H3C1.9 18 1 17.1 1 16V3H0V1H5V0H11ZM3 16H13V3H3V16ZM5 5H7V14H5V5ZM11 5H9V14H11V5Z" fill="currentColor"/>
            </svg>
          </template>
        </ResponsiveButton>
      </div>
    </div>

    <!-- VueFlow Handle - Bottom (Output) -->
    <Handle 
      type="source" 
      :position="Position.Bottom" 
      class="action-handle action-handle--bottom"
      :class="{ 'action-handle--connected': hasOutgoingConnection }"
    >
      <div v-if="!hasOutgoingConnection" class="connector-inner">
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 11.25H11.25V20H8.75V11.25H0V8.75H8.75V0H11.25V8.75H20V11.25Z" fill="#FFFFFF"/>
        </svg>
      </div>
    </Handle>
  </div>
</template>

<script>
import { Handle, Position, useVueFlow } from '@vue-flow/core';

export default {
  name: 'LayoutActionCard',
  components: {
    Handle
  },
  inject: ['isNodeButtonDisabled'],
  props: {
    id: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    },
    isConfigured: {
      type: Boolean,
      default: false
    },
    isConfigureActive: {
      type: Boolean,
      default: true
    },
    selected: {
      type: Boolean,
      default: false
    }
  },
  emits: ['delete', 'configure'],
  setup() {
    const { edges } = useVueFlow();
    return { edges };
  },
  data() {
    return {
      Position
    };
  },
  computed: {
    hasIncomingConnection() {
      return this.edges.some(edge => edge.target === this.id);
    },
    hasOutgoingConnection() {
      return this.edges.some(edge => edge.source === this.id);
    },
    cardClass() {
      const classes = [this.isConfigured ? 'action-card--configured' : 'action-card--not-configured'];
      if (this.selected) {
        classes.push('action-card--selected');
      }
      return classes.join(' ');
    },
    headerClass() {
      return this.isConfigured ? 'action-card-header--configured' : 'action-card-header--not-configured';
    },
    iconClass() {
      return this.isConfigured ? 'action-icon--configured' : 'action-icon--not-configured';
    },
    configureClass() {
      return this.selected? 'btn-configure--active' : 'btn-configure--disabled';
    },
    buttonContainerClass() {
      return '';
    },
    deleteButtonClass() {
      return '';
    }
  },
  methods: {
    onDeleteClick() {
      this.$emit('delete');
    },
    onConfigureClick() {
      this.$emit('configure');
    }
  }
};
</script>

<style scoped>
.action-card {
  width: 324px;
  position: relative;
}

.action-card-body {
  padding: 24px 18px;
  border-radius: 10px 10px 10px 10px;
  box-shadow: 0 1px 15px rgba(164, 164, 164, 0.25);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-card--not-configured .action-card-body {
  background: #FEF9E9;
  border: 1px solid #D10000;
  transition: border-color 0.2s ease;
}

.action-card--configured .action-card-body {
  background: #F9FBFD;
  border: 1px solid #81B0E1;
  transition: border-color 0.2s ease;
}

/* Hover Effect */
.action-card:hover .action-card-body {
  border-color: #004879;
  border-width: 1.5px;
}

/* Selected Effect */
.action-card--selected .action-card-body {
  border-color: #004879;
  border-width: 1.5px;
}

.action-card-header {
  padding: 12px 8px;
  border-radius: 20px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.action-card-header--not-configured {
  background: #FDF0E3;
}

.action-card-header--configured {
  background: #E4F3F5;
}

.action-icon {
  width: 40px;
  height: 40px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-icon--not-configured {
  background: #FEB52E;
  transition: background-color 0.2s ease;
}

.action-icon--configured {
  background: #5587BC;
  transition: background-color 0.2s ease;
}

/* Hover Effect - Icon Background */
.action-card:hover .action-icon {
  background: #004879;
}

/* Selected Effect - Icon Background */
.action-card--selected .action-icon {
  background: #004879;
}

.action-icon-svg {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-icon-svg svg {
  width: 100%;
  height: 100%;
}

.action-header-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: 2px;
}

.action-title {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #374957;
}

.action-subtitle {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  margin-bottom: 4px;
  color: #333;
}

.action-status-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.action-status-icon {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-status-icon svg {
  width: 100%;
  height: 100%;
}

.action-status-text {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}

.action-status-text--error {
  color: #D10000;
}

.action-status-text--success {
  color: #0A6629;
}

.action-buttons {
  display: flex;
  justify-content: center;
  padding-top: 16px;
}

.btn-delete {
  width: 136px;
}

.btn-delete :deep(.responsive-button) {
  width: 100%;
}

.action-handle {
  width: 28px !important;
  height: 28px !important;
  border-radius: 15px;
  border: none;
  left: 50% !important;
  transform: translateX(-50%) !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-handle--top {
  top: -14px !important;
}

.action-handle--bottom {
  bottom: -14px !important;
}

.action-card--not-configured .action-handle--top:not(.action-handle--connected) {
  background: #FFFFFF !important;
  border: 5px solid #FEB52E;
  transition: all 0.2s ease;
}

.action-card--configured .action-handle--top:not(.action-handle--connected) {
  background: #FFFFFF !important;
  border: 5px solid #81B0E1;
  transition: all 0.2s ease;
}

.action-card--not-configured .action-handle--bottom:not(.action-handle--connected) {
  background: #FEB52E !important;
  border: 5px solid #FEB52E;
  transition: all 0.2s ease;
}

.action-card--configured .action-handle--bottom:not(.action-handle--connected) {
  background: #81B0E1 !important;
  border: 5px solid #81B0E1;
  transition: all 0.2s ease;
}

/* Connected state - filled with connection color */
.action-handle--connected {
  background: #6f4999 !important;
  border: none !important;
}

/* Hover Effect - Handlers */
.action-card:hover .action-handle--top:not(.action-handle--connected) {
  border-color: #004879 !important;
}

.action-card:hover .action-handle--bottom:not(.action-handle--connected) {
  border-color: #004879 !important;
  background: #004879 !important;
}

.action-card:hover .action-handle--connected:not(.action-handle--connected) {
  background: #004879 !important;
}

.connector-inner {
  width: 36px;
  height: 36px;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
